// react
import React, { useState } from "react";
import { useInvestors } from "./useCustomState";
import {
  Dialog,
  Drawer,
  OTPInput,
  Select,
  SelectButton,
  TextArea,
  TextField,
} from "../../../components/UI";
import { SubmitButton } from "../../../../Components/Button/AllButton";
import { useCityOptions } from "../../../../hooks/useCityOptions";
import { useWidth } from "../../../hooks/useMobile";
import { OtpTypes } from "../../../../utils/otp";
import { generateRegistrationOTP } from "../../../../services/otp";
import { createInvestor } from "../../../../services/investors";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import { hideLoader, showLoader } from "../../../../actions/UserActions";
import { toastMessage } from "../../../../Components/showMessages/toastMessage";
import { showHttpError } from "../../../../utils/message";

type TDrawerType = "City" | "Type" | "";

const typeOptions = [
  { label: "Local", value: "IntraCity" },
  { label: "Part Load", value: "PartLoad" },
  { label: "Full Load", value: "FullLoad" },
];

const REMARKS_LENGTH = 50;

// FC: Investors
const Investors = () => {
  const {
    city,
    setCity,
    type,
    setType,
    contactNumber,
    setContactNumber,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    remarks,
    setRemarks,
    otp,
    setOtp,
  } = useInvestors();
  const { cityOptions } = useCityOptions();
  const [drawerType, setDrawerType] = useState<TDrawerType>("");
  const { mobileView } = useWidth();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const dispatch = useAppDispatch();

  function handleClickOnSelectButton(type: TDrawerType) {
    setDrawerType(type);
    setIsDrawerOpen(true);
  }

  function handleOptionClick(option: any, drawerType: TDrawerType) {
    if (drawerType === "City") {
      setCity(option);
      setDrawerType("Type");
    } else {
      setType(option);
      setIsDrawerOpen(false);
    }
    setSearchText("");
  }

  let options;
  if (drawerType === "City") {
    options = cityOptions;
  } else {
    options = typeOptions;
  }

  const filteredOptions = options.filter((option: any) =>
    option?.label?.toLowerCase()?.includes(searchText?.toLowerCase())
  );

  const canSendOtp = firstName && lastName && (contactNumber && contactNumber.length === 10) &&type?.value && city?.value && remarks.length > 2;

  async function handleOnSubmit() {
    if(remarks.length < REMARKS_LENGTH) {
      return showHttpError(`Remarks should be atleast ${REMARKS_LENGTH} characters long!`);
    }
    try {
      dispatch(showLoader());
      const otpPayload = {
        type: OtpTypes.InvestorVerification,
        userContact: Number(contactNumber),
      };
      await generateRegistrationOTP(otpPayload);
      toastMessage("OTP  sent successfully!");
      setIsOtpModalOpen(true);
    } catch (error: any) {
      console.log("Error while generating regisration OTP", error);
      showHttpError(error?.message);
    } finally {
      dispatch(hideLoader());
    }
  }

  async function handleOtpSubmitAndCreateInvestor() {
    try {
      dispatch(showLoader());
      const payload = {
        contact: Number(contactNumber),
        city: city?.value || "",
        name: {
          fName: firstName,
          lName: lastName,
        },
        type: type?.value || "",
        otp: Number(otp),
        remark: remarks,
      };
      await createInvestor(payload);
      toastMessage("We'll get back to you!");
      resetForm();
    } catch (error: any) {
      console.log("Error while verifying OTP", error);
      showHttpError(error?.message);
    } finally {
      dispatch(hideLoader());
      setIsOtpModalOpen(false);
    }
  }

  function resetForm() {
    setFirstName("");
    setLastName("");
    setContactNumber("");
    setOtp("");
    setCity({ label: "Select City", value: "" })
    setType({ label: "Select Type", value: "" })
    setRemarks("");
  }

  return (
    <div className="flex-col gap-1">
      <div className="two-column m-one-column gap-1">
        {mobileView ? (
          <>
            <SelectButton
              label="City"
              placeholder="Select City"
              value={city?.label}
              onClick={() => {
                handleClickOnSelectButton("City");
              }}
            />
            <SelectButton
              label="Type"
              placeholder="Select Type"
              value={type?.label}
              onClick={() => {
                handleClickOnSelectButton("Type");
              }}
            />

            <Drawer
              state={isDrawerOpen}
              setState={setIsDrawerOpen}
              position="bottom"
              maxHeight="full"
              drawerBodyClassName="h-100"
              drawerHeading={`Select ${drawerType}`}
              drawerBody={
                <div className="flex-col gap-1">
                  <TextField
                    placeholder={`Search ${drawerType}...`}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  {filteredOptions?.length > 0 &&
                    filteredOptions?.map((option: any) => (
                      <SelectButton
                        value={option?.label}
                        onClick={() => {
                          handleOptionClick(option, drawerType);
                        }}
                      />
                    ))}
                </div>
              }
            />
          </>
        ) : (
          <>
            <Select
              label="City"
              placeholder="Select City"
              options={cityOptions}
              value={city}
              onChange={(e: any) => setCity(e)}
            />
            <Select
              label="Type"
              placeholder="Select Type"
              options={typeOptions}
              value={type}
              onChange={(e: any) => setType(e)}
            />
          </>
        )}
      </div>
      <div className="two-column m-one-column gap-1">
        <TextField
          label="First Name"
          placeholder="Enter First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextField
          label="Last Name"
          placeholder="Enter Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
      </div>
      <TextField
        label="Contact Number"
        type="tel"
        value={contactNumber}
        maxLength={10}
        placeholder="Enter Contact Number"
        onChange={(e) => setContactNumber(e.target.value)}
      />
      <TextArea
        label="Remarks"
        value={remarks}
        placeholder="Tell us about yourself..."
        onChange={(e) => setRemarks(e.target.value)}
      />
      <SubmitButton disabled={!canSendOtp} type="button" onClick={handleOnSubmit} className="flex-center m-w-100" />

      {mobileView ? (
        <Drawer
          state={isOtpModalOpen}
          setState={setIsOtpModalOpen}
          drawerHeading="Enter OTP"
          position="bottom"
          maxHeight="full"
          drawerBody={
            <div className="flex-col gap-1">
              <OTPInput
                label="Enter OTP"
                onOtpComplete={(value) => setOtp(value)}
              />
            </div>
          }
          drawerFooter={
            <SubmitButton
              type="button"
              onClick={handleOtpSubmitAndCreateInvestor}
              fullWidth
            />
          }
        />
      ) : (
        <Dialog
          state={isOtpModalOpen}
          setState={setIsOtpModalOpen}
          header="Enter OTP"
          maxWidth="sm"
          body={
            <div className="flex-col gap-1">
              <OTPInput
                label="Enter OTP"
                onOtpComplete={(value) => setOtp(value)}
              />
            </div>
          }
          footer={
            <SubmitButton
              type="button"
              onClick={handleOtpSubmitAndCreateInvestor}
              action="secondary"
            />
          }
        ></Dialog>
      )}
    </div>
  );
};

export default Investors;
